import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows, FontWeights } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { AnimatedCounter } from '../components/MicroInteractions';

// Main Container
const HubContainer = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  padding: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${Spacing.xxxl}px ${Spacing.xl}px ${Spacing.xl}px;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: ${Spacing.xl}px;
  min-height: calc(100vh - 160px); /* Account for header heights */
  scroll-margin-top: 120px; /* Ensure content starts below sticky headers */
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding-top: ${Spacing.xxl}px;
    gap: ${Spacing.lg}px;
    scroll-margin-top: 100px; /* Adjusted for mobile */
  }
`;

// Main Feed Section
const FeedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px; /* Increased spacing between posts */
  
  /* Ensure consistent vertical rhythm */
  & > * {
    scroll-margin-top: 120px; /* Account for sticky headers */
  }
`;

const FeedCard = styled.div<{ featured?: boolean }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  padding: ${props => props.featured ? Spacing.xxl : Spacing.xl}px;
  box-shadow: 
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  ${props => props.featured && `
    border-left: 4px solid ${Colors.primary};
    background: linear-gradient(135deg, ${Colors.surface} 0%, ${Colors.backgroundSecondary} 100%);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.08),
      0 2px 4px rgba(0, 0, 0, 0.04);
    
    &::before {
      content: '⭐';
      position: absolute;
      top: ${Spacing.md}px;
      right: ${Spacing.md}px;
      font-size: 16px;
      opacity: 0.3;
    }
  `}
  
  &:hover {
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.12),
      0 4px 8px rgba(0, 0, 0, 0.06);
    border-color: ${Colors.borderDark};
    transform: translateY(-3px);
  }
`;

const FeedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.lg}px;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const Avatar = styled.div<{ color?: string; size?: 'md' | 'lg' }>`
  width: ${props => props.size === 'lg' ? '56px' : '52px'};
  height: ${props => props.size === 'lg' ? '56px' : '52px'};
  border-radius: 50%;
  background: ${props => props.color || Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: ${props => props.size === 'lg' ? FontSizes.xl : FontSizes.lg}px;
  border: 3px solid ${Colors.surface};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const AuthorDetails = styled.div``;

const AuthorName = styled.div`
  font-size: ${FontSizes.md}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
`;

const PostMeta = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const PostType = styled.span<{ type: 'story' | 'recognition' | 'event' | 'achievement' }>`
  display: inline-block;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
  margin-left: ${Spacing.sm}px;
  
  ${props => {
    switch(props.type) {
      case 'story':
        return `background: ${Colors.info}20; color: ${Colors.info};`;
      case 'recognition':
        return `background: ${Colors.success}20; color: ${Colors.success};`;
      case 'event':
        return `background: ${Colors.accent}20; color: ${Colors.accent};`;
      case 'achievement':
        return `background: ${Colors.warning}20; color: ${Colors.warning};`;
      default:
        return `background: ${Colors.surfaceHover}; color: ${Colors.textSecondary};`;
    }
  }}
`;

const FeedContent = styled.div`
  margin-bottom: ${Spacing.lg}px;
`;

const PostTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.md}px;
`;

const PostText = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
`;

const PostImage = styled.img`
  width: 100%;
  border-radius: ${BorderRadius.md}px;
  margin-top: ${Spacing.md}px;
  max-height: 400px;
  object-fit: cover;
`;

const PlaceholderImage = styled.div<{ type: string; featured?: boolean }>`
  width: 100%;
  height: ${props => props.featured ? '300px' : '240px'};
  border-radius: ${BorderRadius.md}px;
  margin-top: ${Spacing.md}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.md}px;
  background: ${props => {
    switch(props.type) {
      case 'recognition': return `linear-gradient(135deg, ${Colors.success}15, ${Colors.success}08, ${Colors.success}03)`;
      case 'achievement': return `linear-gradient(135deg, ${Colors.warning}15, ${Colors.warning}08, ${Colors.warning}03)`;
      case 'event': return `linear-gradient(135deg, ${Colors.accent}15, ${Colors.accent}08, ${Colors.accent}03)`;
      default: return `linear-gradient(135deg, ${Colors.primary}15, ${Colors.primary}08, ${Colors.primary}03)`;
    }
  }};
  border: 1px solid ${props => {
    switch(props.type) {
      case 'recognition': return `${Colors.success}25`;
      case 'achievement': return `${Colors.warning}25`;
      case 'event': return `${Colors.accent}25`;
      default: return `${Colors.primary}25`;
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'recognition': return Colors.successDark;
      case 'achievement': return Colors.warningDark;
      case 'event': return Colors.accent;
      default: return Colors.primary;
    }
  }};
  font-size: ${props => props.featured ? '64px' : '48px'};
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '${props => {
      switch(props.type) {
        case 'recognition': return '🏆';
        case 'achievement': return '🎯';
        case 'event': return '📅';
        default: return '📖';
      }
    }}';
    position: absolute;
    top: ${Spacing.md}px;
    right: ${Spacing.md}px;
    font-size: 20px;
    opacity: 0.4;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, currentColor 0px, transparent 120px);
    opacity: 0.05;
  }
`;

const PlaceholderText = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: 500;
  color: currentColor;
  opacity: 0.7;
  text-align: center;
  margin-top: ${Spacing.xs}px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs}px;
  margin-top: ${Spacing.md}px;
`;

const Tag = styled.span`
  padding: 6px 12px;
  background: ${Colors.backgroundTertiary};
  border: 1px solid ${Colors.borderLight};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: ${Colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.1px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '#';
    opacity: 0.6;
  }
  
  &:hover {
    background: ${Colors.primary}10;
    border-color: ${Colors.primary}30;
    color: ${Colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FeedActions = styled.div`
  display: flex;
  gap: ${Spacing.xs}px;
  padding-top: ${Spacing.lg}px;
  margin-top: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
  justify-content: flex-start;
  align-items: center;
  
  @media (max-width: 640px) {
    flex-wrap: wrap;
    gap: ${Spacing.sm}px;
  }
`;

const ActionButton = styled.button<{ variant?: 'like' | 'comment' | 'share'; active?: boolean }>`
  background: ${props => props.active ? (
    props.variant === 'like' ? `${Colors.danger}12` :
    props.variant === 'comment' ? `${Colors.info}12` :
    props.variant === 'share' ? `${Colors.success}12` :
    `${Colors.primary}12`
  ) : Colors.backgroundTertiary};
  border: 1px solid ${props => props.active ? (
    props.variant === 'like' ? `${Colors.danger}25` :
    props.variant === 'comment' ? `${Colors.info}25` :
    props.variant === 'share' ? `${Colors.success}25` :
    `${Colors.primary}25`
  ) : Colors.borderLight};
  color: ${props => props.active ? (
    props.variant === 'like' ? Colors.danger :
    props.variant === 'comment' ? Colors.info :
    props.variant === 'share' ? Colors.success :
    Colors.primary
  ) : Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.lg}px;
  transition: all 0.2s ease;
  position: relative;
  min-width: 70px;
  justify-content: center;
  
  &:hover {
    background: ${props => 
      props.variant === 'like' ? `${Colors.danger}15` :
      props.variant === 'comment' ? `${Colors.info}15` :
      props.variant === 'share' ? `${Colors.success}15` :
      Colors.surfaceHover
    };
    border-color: ${props => 
      props.variant === 'like' ? `${Colors.danger}40` :
      props.variant === 'comment' ? `${Colors.info}40` :
      props.variant === 'share' ? `${Colors.success}40` :
      Colors.borderDark
    };
    color: ${props => 
      props.variant === 'like' ? Colors.dangerDark :
      props.variant === 'comment' ? Colors.infoDark :
      props.variant === 'share' ? Colors.successDark :
      Colors.primary
    };
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ReactionSelector = styled.div`
  position: relative;
  display: inline-block;
`;

const QuickReactions = styled.div`
  position: absolute;
  top: -40px;
  left: 0;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xs}px;
  display: flex;
  gap: ${Spacing.xs}px;
  box-shadow: ${Shadows.lg};
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  transition: all 0.2s ease;
  z-index: 10;
  
  &.visible {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
  
  button {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    font-size: 18px;
    cursor: pointer;
    border-radius: ${BorderRadius.sm}px;
    transition: all 0.1s ease;
    
    &:hover {
      background: ${Colors.surfaceHover};
      transform: scale(1.2);
    }
  }
`;

// Sidebar Section
const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

const SidebarCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: 
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.08),
      0 2px 4px rgba(0, 0, 0, 0.04);
    border-color: ${Colors.borderDark};
  }
`;

const SidebarTitle = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.md}px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.md}px;
`;

const StatCard = styled.div`
  text-align: center;
  padding: ${Spacing.md}px;
  background: ${Colors.background};
  border-radius: ${BorderRadius.sm}px;
`;

const StatValue = styled.div`
  font-size: ${FontSizes.xl}px;
  font-weight: 700;
  color: ${Colors.primary};
  margin-bottom: ${Spacing.xs}px;
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

const EventItem = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px;
  background: ${Colors.background};
  border-radius: ${BorderRadius.sm}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const EventDate = styled.div<{ hasImage?: boolean }>`
  width: 48px;
  height: 48px;
  background: ${props => props.hasImage ? 
    `linear-gradient(135deg, ${Colors.primary}, ${Colors.accent})` : 
    `${Colors.primary}10`};
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.hasImage ? 'white' : Colors.primary};
  font-weight: 600;
  position: relative;
  overflow: hidden;
  
  ${props => props.hasImage && `
    &::before {
      content: '📅';
      position: absolute;
      top: 2px;
      right: 2px;
      font-size: 12px;
      opacity: 0.7;
    }
  `}
`;

const EventMonth = styled.div`
  font-size: ${FontSizes.xs}px;
  text-transform: uppercase;
`;

const EventDay = styled.div`
  font-size: ${FontSizes.lg}px;
`;

const EventDetails = styled.div`
  flex: 1;
`;

const EventTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const EventTime = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const CommunityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

const CommunityItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  padding: ${Spacing.sm}px;
  cursor: pointer;
  border-radius: ${BorderRadius.sm}px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const CommunityIcon = styled.div<{ color: string }>`
  width: 36px;
  height: 36px;
  border-radius: ${BorderRadius.sm}px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
  border: 1px solid ${props => props.color}30;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.color};
    opacity: 0.8;
  }
`;

const CommunityInfo = styled.div`
  flex: 1;
`;

const CommunityName = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
`;

const CommunityMembers = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const MemberAvatars = styled.div`
  display: flex;
  margin-left: ${Spacing.xs}px;
  
  & > div {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${Colors.surface};
    margin-left: -6px;
    font-size: 8px;
    
    &:first-child {
      margin-left: 0;
    }
  }
`;

const JoinButton = styled.button`
  padding: ${Spacing.xs}px ${Spacing.md}px;
  background: ${Colors.primary}10;
  color: ${Colors.primary};
  border: 1px solid ${Colors.primary}30;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primary};
    color: white;
  }
  
  &.joined {
    background: ${Colors.success}10;
    color: ${Colors.success};
    border-color: ${Colors.success}30;
  }
`;

// Recognition Modal
const RecognitionButton = styled.button`
  position: fixed;
  bottom: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${Colors.primary};
  color: white;
  border: none;
  box-shadow: ${Shadows.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xl}px;
  transition: all 0.2s ease;
  z-index: 100;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${Shadows.xl};
  }
`;

interface FeedPost {
  id: string;
  type: 'story' | 'recognition' | 'event' | 'achievement';
  author: {
    name: string;
    department: string;
    initials: string;
    color: string;
  };
  title: string;
  content: string;
  image?: string;
  tags: string[];
  likes: number;
  comments: number;
  timestamp: Date;
  liked?: boolean;
}

interface UpcomingEvent {
  id: string;
  title: string;
  date: Date;
  time: string;
  location: string;
  attendees: number;
}

interface Community {
  id: string;
  name: string;
  icon: string;
  color: string;
  members: number;
  joined: boolean;
}

const BelongingHubScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userName, department } = useSelector((state: RootState) => state.user);
  
  const [feedPosts, setFeedPosts] = useState<FeedPost[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [belongingScore, setBelongingScore] = useState(8.2);
  const [recognitionsSent, setRecognitionsSent] = useState(12);
  const [eventsAttended, setEventsAttended] = useState(5);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showQuickReactions, setShowQuickReactions] = useState<string | null>(null);
  const [quickReactionTimeouts, setQuickReactionTimeouts] = useState<{[key: string]: ReturnType<typeof setTimeout>}>({});

  useEffect(() => {
    loadBelongingData();
  }, []);

  const loadBelongingData = () => {
    // Mock data for the feed
    const mockPosts: FeedPost[] = [
      {
        id: '1',
        type: 'recognition',
        author: {
          name: 'Sarah Chen',
          department: 'Engineering',
          initials: 'SC',
          color: Colors.primary
        },
        title: 'Kudos to the DevOps Team! 🎉',
        content: 'Huge thanks to Michael, Lisa, and the entire DevOps team for their incredible work on the infrastructure migration. Your dedication over the weekend ensured zero downtime for our customers. You truly embody our value of "Customer First"!',
        tags: ['#TeamWork', '#CustomerFirst', '#Excellence'],
        likes: 47,
        comments: 12,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        liked: false
      },
      {
        id: '2',
        type: 'story',
        author: {
          name: 'John Davis',
          department: 'HR',
          initials: 'JD',
          color: Colors.success
        },
        title: 'Welcome Our New Mars Explorers! 🚀',
        content: 'This week, we welcomed 15 new team members across Engineering, Sales, and Operations. Each brings unique perspectives and skills that will help us reach new heights. Join me in making them feel at home in our Clouds on Mars family!',
        image: '/api/placeholder/600/300',
        tags: ['#NewHires', '#Welcome', '#Growth'],
        likes: 89,
        comments: 23,
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
        liked: true
      },
      {
        id: '3',
        type: 'achievement',
        author: {
          name: 'Marketing Team',
          department: 'Marketing',
          initials: 'MT',
          color: Colors.warning
        },
        title: 'Industry Recognition: Best Workplace Award 2024',
        content: 'Proud moment! Clouds on Mars has been recognized as one of the "Top 10 Best Workplaces in Tech" for 2024. This achievement reflects our collective commitment to creating an inclusive, innovative, and supportive environment. Thank you all for making this possible!',
        tags: ['#Award', '#BestWorkplace', '#Proud'],
        likes: 234,
        comments: 45,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
        liked: true
      },
      {
        id: '4',
        type: 'event',
        author: {
          name: 'People & Culture',
          department: 'HR',
          initials: 'PC',
          color: Colors.accent
        },
        title: 'Join Us: Global Town Hall - Q4 Results & 2025 Vision',
        content: 'Mark your calendars! Our quarterly Town Hall is scheduled for next Thursday at 3 PM CET. CEO Maria Rodriguez will share Q4 achievements, 2025 strategic priorities, and answer your questions live. Virtual attendance link will be shared via email.',
        tags: ['#TownHall', '#Strategy', '#AllHands'],
        likes: 156,
        comments: 8,
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000),
        liked: false
      }
    ];

    const mockEvents: UpcomingEvent[] = [
      {
        id: '1',
        title: 'Global Town Hall',
        date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        time: '3:00 PM CET',
        location: 'Virtual + Berlin Office',
        attendees: 1247
      },
      {
        id: '2',
        title: 'Tech Talk: AI in HR',
        date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
        time: '12:00 PM CET',
        location: 'Virtual',
        attendees: 89
      },
      {
        id: '3',
        title: 'Team Building: Escape Room',
        date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        time: '5:30 PM CET',
        location: 'Berlin Office',
        attendees: 24
      }
    ];

    const mockCommunities: Community[] = [
      {
        id: '1',
        name: 'Women in Tech',
        icon: '👩‍💻',
        color: Colors.accent,
        members: 234,
        joined: true
      },
      {
        id: '2',
        name: 'Mars Runners',
        icon: '🏃‍♀️',
        color: Colors.success,
        members: 156,
        joined: false
      },
      {
        id: '3',
        name: 'Book Club',
        icon: '📚',
        color: Colors.info,
        members: 89,
        joined: false
      },
      {
        id: '4',
        name: 'Green Initiative',
        icon: '🌱',
        color: Colors.success,
        members: 312,
        joined: true
      }
    ];

    setFeedPosts(mockPosts);
    setUpcomingEvents(mockEvents);
    setCommunities(mockCommunities);
  };

  const handleLikePost = (postId: string) => {
    setFeedPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, liked: !post.liked, likes: post.liked ? post.likes - 1 : post.likes + 1 }
        : post
    ));
  };

  const handleJoinCommunity = (communityId: string) => {
    setCommunities(prev => prev.map(community =>
      community.id === communityId
        ? { ...community, joined: !community.joined, members: community.joined ? community.members - 1 : community.members + 1 }
        : community
    ));
  };

  const handleQuickReaction = (postId: string, emoji: string) => {
    // In a real app, this would send the reaction to the backend
    console.log(`Added ${emoji} reaction to post ${postId}`);
    
    // For demo purposes, just increase the like count
    setFeedPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, likes: post.likes + 1 }
        : post
    ));
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(quickReactionTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [quickReactionTimeouts]);

  const formatTimestamp = (date: Date): string => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const formatEventDate = (date: Date): { month: string; day: string } => {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return {
      month: months[date.getMonth()],
      day: date.getDate().toString()
    };
  };

  return (
    <HubContainer>
      <UnifiedPageHeader
        title="Belonging Hub"
        subtitle="Connect, celebrate, and grow together"
        metadata={{
          timestamp: new Date(),
          count: feedPosts.length,
          status: 'Live'
        }}
        actions={[
          {
            label: t('belonging.resources', 'Resources'),
            onClick: () => navigate('/belonging/resources'),
            variant: 'secondary',
            icon: ''
          },
          {
            label: t('belonging.managerInsights', 'Team Insights'),
            onClick: () => navigate('/belonging/manager-insights'),
            variant: 'secondary',
            icon: ''
          }
        ]}
      />

      <ContentWrapper>
        <FeedSection>
          {feedPosts.map((post, index) => {
            const isFeatured = post.type === 'achievement' || post.likes > 100;
            const avatarSize = isFeatured ? 'lg' : 'md';
            
            return (
              <FeedCard key={post.id} featured={isFeatured}>
                <FeedHeader>
                  <AuthorInfo>
                    <Avatar color={post.author.color} size={avatarSize}>
                      {post.author.initials}
                    </Avatar>
                    <AuthorDetails>
                      <AuthorName>{post.author.name}</AuthorName>
                      <PostMeta>
                        {post.author.department} • {formatTimestamp(post.timestamp)}
                        <PostType type={post.type}>
                          {post.type === 'recognition' ? 'Recognition' :
                           post.type === 'story' ? 'Story' :
                           post.type === 'event' ? 'Event' :
                           'Achievement'}
                        </PostType>
                      </PostMeta>
                    </AuthorDetails>
                  </AuthorInfo>
                </FeedHeader>

                <FeedContent>
                  <PostTitle>{post.title}</PostTitle>
                  <PostText>{post.content}</PostText>
                  {post.image ? (
                    <PostImage src={post.image} alt={post.title} />
                  ) : (
                    <PlaceholderImage type={post.type} featured={isFeatured}>
                      {post.type === 'recognition' ? '🏆' :
                       post.type === 'story' ? '📖' :
                       post.type === 'event' ? '📅' :
                       post.type === 'achievement' ? '🎉' : '✨'}
                      <PlaceholderText>
                        {post.type === 'recognition' ? 'Team Recognition' :
                         post.type === 'story' ? 'Company Story' :
                         post.type === 'event' ? 'Upcoming Event' :
                         post.type === 'achievement' ? 'Achievement Unlocked' : 'Update'}
                      </PlaceholderText>
                    </PlaceholderImage>
                  )}
                  <TagList>
                    {post.tags.map(tag => (
                      <Tag key={tag} onClick={() => setActiveFilter(tag)}>
                        {tag.replace('#', '')}
                      </Tag>
                    ))}
                  </TagList>
                </FeedContent>

                <FeedActions>
                  <ReactionSelector 
                    onMouseEnter={() => {
                      if (quickReactionTimeouts[post.id]) {
                        clearTimeout(quickReactionTimeouts[post.id]);
                      }
                      setShowQuickReactions(post.id);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setShowQuickReactions(null);
                      }, 300);
                      setQuickReactionTimeouts(prev => ({ ...prev, [post.id]: timeout }));
                    }}
                  >
                    <ActionButton 
                      variant="like"
                      active={post.liked}
                      onClick={() => handleLikePost(post.id)}
                    >
                      {post.liked ? '❤️' : '🤍'} {post.likes}
                    </ActionButton>
                    <QuickReactions className={showQuickReactions === post.id ? 'visible' : ''}>
                      <button 
                        onClick={() => {
                          handleQuickReaction(post.id, '👍');
                          setShowQuickReactions(null);
                        }}
                        title="Thumbs up"
                      >
                        👍
                      </button>
                      <button 
                        onClick={() => {
                          handleQuickReaction(post.id, '❤️');
                          setShowQuickReactions(null);
                        }}
                        title="Love"
                      >
                        ❤️
                      </button>
                      <button 
                        onClick={() => {
                          handleQuickReaction(post.id, '🎉');
                          setShowQuickReactions(null);
                        }}
                        title="Celebrate"
                      >
                        🎉
                      </button>
                      <button 
                        onClick={() => {
                          handleQuickReaction(post.id, '👏');
                          setShowQuickReactions(null);
                        }}
                        title="Applause"
                      >
                        👏
                      </button>
                      <button 
                        onClick={() => {
                          handleQuickReaction(post.id, '🚀');
                          setShowQuickReactions(null);
                        }}
                        title="Rocket"
                      >
                        🚀
                      </button>
                    </QuickReactions>
                  </ReactionSelector>
                  <ActionButton 
                    variant="comment"
                    onClick={() => console.log(`Open comments for post ${post.id}`)}
                  >
                    💬 {post.comments}
                  </ActionButton>
                  <ActionButton 
                    variant="share"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: post.title,
                          text: post.content,
                          url: window.location.href
                        }).catch(console.error);
                      } else {
                        navigator.clipboard.writeText(window.location.href)
                          .then(() => console.log('Link copied to clipboard'))
                          .catch(console.error);
                      }
                    }}
                  >
                    🔗 Share
                  </ActionButton>
                </FeedActions>
              </FeedCard>
            );
          })}
        </FeedSection>

        <Sidebar>
          <SidebarCard>
            <SidebarTitle>Your Belonging Score</SidebarTitle>
            <StatGrid>
              <StatCard>
                <StatValue>
                  <AnimatedCounter value={belongingScore} formatter={(v) => v.toFixed(1)} />
                </StatValue>
                <StatLabel>Overall Score</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>
                  <AnimatedCounter value={recognitionsSent} />
                </StatValue>
                <StatLabel>Kudos Sent</StatLabel>
              </StatCard>
            </StatGrid>
            <StatCard>
              <StatValue>
                <AnimatedCounter value={eventsAttended} />
              </StatValue>
              <StatLabel>Events This Quarter</StatLabel>
            </StatCard>
          </SidebarCard>

          <SidebarCard>
            <SidebarTitle>Upcoming Events</SidebarTitle>
            <EventList>
              {upcomingEvents.map(event => {
                const { month, day } = formatEventDate(event.date);
                const hasImage = Math.random() > 0.5; // Simulate some events having images
                return (
                  <EventItem key={event.id}>
                    <EventDate hasImage={hasImage}>
                      <EventMonth>{month}</EventMonth>
                      <EventDay>{day}</EventDay>
                    </EventDate>
                    <EventDetails>
                      <EventTitle>{event.title}</EventTitle>
                      <EventTime>
                        🕒 {event.time} • 👥 {event.attendees} attending
                      </EventTime>
                    </EventDetails>
                  </EventItem>
                );
              })}
            </EventList>
          </SidebarCard>

          <SidebarCard>
            <SidebarTitle>Communities</SidebarTitle>
            <CommunityList>
              {communities.map(community => {
                // Generate some sample member avatars
                const memberColors = [Colors.primary, Colors.success, Colors.warning, Colors.accent];
                const memberInitials = ['JD', 'SC', 'AM', 'RK'];
                
                return (
                  <CommunityItem key={community.id}>
                    <CommunityIcon color={community.color}>
                      {community.icon}
                    </CommunityIcon>
                    <CommunityInfo>
                      <CommunityName>{community.name}</CommunityName>
                      <CommunityMembers>
                        {community.members} members
                        <MemberAvatars>
                          {memberInitials.slice(0, 3).map((initials, index) => (
                            <Avatar 
                              key={index}
                              color={memberColors[index]} 
                              style={{ 
                                width: '20px', 
                                height: '20px', 
                                fontSize: '8px',
                                border: '2px solid white'
                              }}
                            >
                              {initials}
                            </Avatar>
                          ))}
                          {community.members > 3 && (
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: Colors.textTertiary,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '8px',
                              border: '2px solid white',
                              marginLeft: '-6px'
                            }}>
                              +{community.members - 3}
                            </div>
                          )}
                        </MemberAvatars>
                      </CommunityMembers>
                    </CommunityInfo>
                    <JoinButton 
                      className={community.joined ? 'joined' : ''}
                      onClick={() => handleJoinCommunity(community.id)}
                    >
                      {community.joined ? '✓ Joined' : 'Join'}
                    </JoinButton>
                  </CommunityItem>
                );
              })}
            </CommunityList>
          </SidebarCard>
        </Sidebar>
      </ContentWrapper>

      <RecognitionButton onClick={() => console.log('Open recognition modal')}>
        +
      </RecognitionButton>
    </HubContainer>
  );
};

export default BelongingHubScreen;